import { Check, ChevronDown, ChevronUp, Clipboard } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import type { PublishState } from "../data/cockpit-data";
import { NOTHING_IN_FORCE, publishPresentation } from "../data/item-publish-state";
import { badgeVariant, type Tone } from "../format";

/**
 * Whether something is really live on Amazon — the single most consequential
 * distinction on this surface.
 *
 * Meteoric encoded it in four colours. Here it is the LABEL plus a badge
 * weight, because "Ledger only" and "Live on Amazon" being two shades of the
 * same green is exactly how a reader concludes ads changed when they did not.
 * `unknown` is the one carried as a problem: an unreadable publish journal is
 * not a quiet state, it means nobody can say what is on the account.
 */
const PUBLISH_TONE: Record<PublishState, Tone> = {
  published: "ok",
  not_published: "neutral",
  reversed: "watch",
  unknown: "critical",
};

export function AmazonStatePill({
  state,
  targetRef,
}: {
  state: PublishState | "nothing" | "retired";
  targetRef?: string | null;
}) {
  const [copied, setCopied] = useState(false);

  if (state === "nothing") {
    return <span className="whitespace-nowrap text-xs text-muted-foreground">Nothing applied</span>;
  }
  if (state === "retired") {
    return (
      <span className="whitespace-nowrap text-xs text-muted-foreground" title={NOTHING_IN_FORCE.title}>
        {NOTHING_IN_FORCE.label}
      </span>
    );
  }

  const presentation = publishPresentation(state);
  const settled = state === "published" || state === "reversed";

  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <Badge variant={badgeVariant(PUBLISH_TONE[state])} title={presentation.title}>
        {presentation.label}
      </Badge>
      {settled && targetRef ? (
        // The target_ref is the receipt — the Amazon keyword id the write created.
        // It is copyable because the next thing anyone does with it is paste it
        // into the Amazon console or a support thread.
        <Button
          variant="outline"
          size="xs"
          className="max-w-40 font-mono"
          title={`Copy target_ref ${targetRef}`}
          aria-label={`Copy target reference ${targetRef}`}
          onClick={() => {
            void navigator.clipboard.writeText(targetRef).then(() => {
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1600);
            });
          }}
        >
          <span className="truncate">target_ref: {targetRef}</span>
          {copied ? <Check aria-hidden="true" /> : <Clipboard aria-hidden="true" />}
        </Button>
      ) : settled ? (
        <span className="whitespace-nowrap font-mono text-[10px] text-muted-foreground">
          target_ref unavailable
        </span>
      ) : null}
    </span>
  );
}

/**
 * A sortable column heading.
 *
 * `aria-sort` is on the `th` rather than the button: assistive tech reads the
 * sort state off the column, and a reader who cannot see the chevron otherwise
 * has no way to know which column ordered the table.
 */
export function SortableHeader<Key extends string>({
  label,
  sortKey,
  activeKey,
  direction,
  onSort,
  align = "left",
  title,
}: {
  label: string;
  sortKey: Key;
  activeKey: Key;
  direction: "asc" | "desc";
  onSort: (key: Key) => void;
  align?: "left" | "right";
  title?: string;
}) {
  const active = activeKey === sortKey;
  return (
    <th
      scope="col"
      title={title}
      aria-sort={active ? (direction === "asc" ? "ascending" : "descending") : undefined}
      className={`px-3 py-2 font-medium ${align === "right" ? "text-right" : "text-left"}`}
    >
      <Button
        type="button"
        variant="ghost"
        size="xs"
        onClick={() => onSort(sortKey)}
        className={`-mx-2 uppercase tracking-wide text-muted-foreground ${align === "right" ? "ml-auto" : ""}`}
      >
        {label}
        {active ? direction === "asc" ? <ChevronUp aria-hidden="true" /> : <ChevronDown aria-hidden="true" /> : null}
      </Button>
    </th>
  );
}

export const PAGE_SIZE = 50;

/**
 * The page footer for this module's own tables.
 *
 * These reads return the WHOLE collection — `/state/item-status` and
 * `/state/change-log` have no offset — so the slicing is done here, over rows
 * already in hand. It is deliberately NOT the shell `DataTable`'s server
 * pagination: pretending a client-side slice is a server page would report a
 * `rowCount` the server never promised.
 */
export function TablePagination({
  page,
  pageCount,
  visibleCount,
  onPage,
}: {
  page: number;
  pageCount: number;
  visibleCount: number;
  onPage: (page: number) => void;
}) {
  if (visibleCount <= PAGE_SIZE) return null;
  return (
    <footer className="flex items-center justify-between border-t border-ks-grey-3 px-4 py-2.5 text-sm text-muted-foreground">
      <span className="tabular-nums">
        Showing {(page * PAGE_SIZE + 1).toLocaleString("en-US")}&ndash;
        {Math.min((page + 1) * PAGE_SIZE, visibleCount).toLocaleString("en-US")} of{" "}
        {visibleCount.toLocaleString("en-US")}
      </span>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => onPage(page - 1)} disabled={page === 0}>
          Previous
        </Button>
        <span className="tabular-nums text-[13px]">
          Page {page + 1} / {pageCount}
        </span>
        <Button variant="outline" size="sm" onClick={() => onPage(page + 1)} disabled={page >= pageCount - 1}>
          Next
        </Button>
      </div>
    </footer>
  );
}
